/**
 * The player's identity on this device.
 *
 * Storage is split deliberately:
 *  - The seat a tab currently holds lives in sessionStorage, so two tabs on one
 *    device are two independent players.
 *  - Every seat this browser has owned per room is additionally remembered in
 *    localStorage, so a closed (or evicted) tab can get its seat back instead of
 *    returning as a new player with zero points.
 * On rejoin the client offers all remembered seats and the server picks one that
 * is actually free — a seat another tab is sitting in is never handed over.
 */

import { MAX_SEAT_TOKENS } from '../shared/constants';

const NAME_KEY = 'loftjo_name';
const ROOM_KEY = 'loftjo_room';
const LAST_NAME_KEY = 'loftjo_last_name';
const LAST_ROOM_KEY = 'loftjo_last_room';
const SEATS_KEY = 'loftjo_seats';
const tokenKey = (room: string): string => `loftjo_token:${room}`;

/** Storage throws in Safari's private mode — identity must never break the join. */
function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function write(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    /* full or blocked — we simply forget this seat */
  }
}

/** Name/room THIS tab plays under — per tab, so two tabs are two players. */
export const getName = (): string | null => read(sessionStorage, NAME_KEY);
export const getRoom = (): string | null => read(sessionStorage, ROOM_KEY);

export const setNameRoom = (name: string, room: string): void => {
  write(sessionStorage, NAME_KEY, name);
  write(sessionStorage, ROOM_KEY, room);
  // Device-wide copies, used only to pre-fill the lobby in a brand-new tab.
  write(localStorage, LAST_NAME_KEY, name);
  write(localStorage, LAST_ROOM_KEY, room);
};

/** Lobby pre-fill: what this tab used, else the last values used on this device. */
export const lobbyName = (): string | null => getName() ?? read(localStorage, LAST_NAME_KEY);
export const lobbyRoom = (): string | null => getRoom() ?? read(localStorage, LAST_ROOM_KEY);

// ── Seat tokens ─────────────────────────────────────────────────────────────

type SeatMap = Record<string, string[]>;

function readSeats(): SeatMap {
  const raw = read(localStorage, SEATS_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: SeatMap = {};
    for (const [room, tokens] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(tokens)) out[room] = tokens.filter((t): t is string => typeof t === 'string');
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Records the seat the server just handed us: as this tab's active seat, and in
 * the browser-wide list for this room (newest first, capped).
 */
export function rememberSeat(room: string, token: string): void {
  write(sessionStorage, tokenKey(room), token);
  const seats = readSeats();
  const rest = (seats[room] ?? []).filter((t) => t !== token);
  seats[room] = [token, ...rest].slice(0, MAX_SEAT_TOKENS);
  write(localStorage, SEATS_KEY, JSON.stringify(seats));
}

/**
 * The seat THIS tab currently holds in `room`, if any. Lives in sessionStorage,
 * so it is by construction unique to this tab — which is what lets the server
 * treat it as a strong claim and hand the seat back even while the previous
 * socket is still winding down (navigation lobby → game page, reload mid-game).
 */
export const ownSeat = (room: string): string | undefined =>
  read(sessionStorage, tokenKey(room)) ?? undefined;

/** Seats to offer when joining `room`: this tab's first, then the older ones. */
export function seatTokens(room: string): string[] {
  const mine = read(sessionStorage, tokenKey(room));
  const seats = readSeats()[room] ?? [];
  const ordered = mine ? [mine, ...seats.filter((t) => t !== mine)] : seats;
  return ordered.slice(0, MAX_SEAT_TOKENS);
}

/** Footer text shown on the lobby and game screens (version + copyright). */
export function footerText(): string {
  return `v${__APP_VERSION__} · © ${new Date().getFullYear()} Einloft Software`;
}

export function escHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
